<template>
  <div class="interview-slot-selection">
    <h1 class="mt-4 mb-3">Interview Slot</h1>
    <ValidationObserver ref="interviewSlot">
      <ValidationProvider v-slot="{ errors }" rules="required">
        <Select
          autocomplete
          :error-messages="showValidationErrors ? errors : ''"
          :value="interviewSlotData.candidate"
          @input="val => updateData('candidate', val)"
          :disabled="isLoadingBookingCandidates"
          :items="formattedWorkers"
          item-text="fullName"
          item-value="id"
          height="50"
          chips
          small-chips
          :loading="isLoadingBookingCandidates"
          placeholder="Select Candidate"
          label="Candidate"
        >
          <template v-slot:selection="{ data }">
            <v-chip close @click:close="interviewSlotData.candidate = null">
              <v-icon>mdi-account</v-icon>
              {{ data.item.fullName }}
            </v-chip>
          </template>
          <template v-slot:item="{ item }">
            <StatusItem class="user-select-item">
              <template v-slot:icon><v-icon>mdi-account</v-icon></template>
              {{ item.fullName }}
            </StatusItem>
          </template>
        </Select>
      </ValidationProvider>
      <ValidationProvider v-slot="{ errors }" rules="required">
        <Select
          label="Interviewers"
          autocomplete
          :error-messages="showValidationErrors ? errors : ''"
          :value="interviewSlotData.interviewers"
          @input="val => updateData('interviewers', val)"
          :disabled="isFetchingInterviewers || !interviewFormatId"
          :items="formattedInterviewers"
          item-text="fullName"
          item-value="id"
          chips
          small-chips
          :loading="isFetchingInterviewers"
          placeholder="Select Interviewers"
          multiple
        >
          <template v-slot:selection="{ data }">
            <v-chip close @click:close="unselectInterviewer(data.item.id)">
              <v-icon>mdi-account</v-icon>
              {{ data.item.fullName }}
            </v-chip>
          </template>
          <template v-slot:item="{ item }">
            <StatusItem class="user-select-item">
              <template v-slot:icon><v-icon>mdi-account</v-icon></template>
              {{ item.fullName }}
            </StatusItem>
          </template>
        </Select>
      </ValidationProvider>
      <ScheduleInterviewSlotSelection
        v-if="size(interviewSlotData.interviewers)"
        :interviewFormatId="interviewFormatId"
        :interviewers="interviewSlotData.interviewers"
        :showValidationErrors="showValidationErrors"
        @onSelectSlot="val => updateData('slot', val)"
      />
    </ValidationObserver>
  </div>
</template>

<script>
import { INTERVIEWS_NAMESPACE } from "@/store/modules/bookings/interviews";
import { FETCH_INTERVIEWERS } from "@/store/modules/bookings/interviews/actions";
import { createNamespacedHelpers } from "vuex";
import StatusItem from "@/components/common/StatusItem";
import Select from "@/components/common/Select";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { copyState } from "@/utils/helpers";
import {
  BOOKINGS_NAMESPACE,
  FETCH_CURRENT_BOOKING_CANDIDATES
} from "@/store/modules/bookings/actions";
import {
  GET_CURRENT_BOOKING_CANDIDATES,
  IS_LOADING_BOOKING_CANDIDATES
} from "@/store/modules/bookings/getters";
import { getCandidateName, getFullName } from "@/utils/users";
import { map, size, filter } from "lodash";
import ScheduleInterviewSlotSelection from "@/views/bookings/BookingInterviews/components/dialogs/ScheduleInterview/components/ScheduleInterviewSlotSelection";

const {
  mapActions: mapInterviewActions,
  mapState: mapInterviewState
} = createNamespacedHelpers(INTERVIEWS_NAMESPACE);
const {
  mapActions: mapBookingActions,
  mapGetters: mapBookingGetters
} = createNamespacedHelpers(BOOKINGS_NAMESPACE);

export default {
  components: {
    StatusItem,
    Select,
    ValidationProvider,
    ValidationObserver,
    ScheduleInterviewSlotSelection
  },
  props: {
    interviewFormatId: {
      type: String,
      required: true
    }
  },
  created() {
    const params = {
      filter: { status: ["unsorted", "evaluated", "shortlisted", "offered"] }
    };
    this.fetchInterviewers(this.interviewFormatId);
    this.fetchCurrentBookingApplications({
      bookingId: this.currentBookingId,
      params
    });
  },
  data() {
    return {
      interviewSlotData: {
        interviewers: [],
        candidate: null,
        slot: null
      },
      showValidationErrors: false
    };
  },
  computed: {
    ...mapBookingGetters({
      allApplications: GET_CURRENT_BOOKING_CANDIDATES,
      isLoadingBookingCandidates: IS_LOADING_BOOKING_CANDIDATES
    }),
    formattedWorkers() {
      return map(this.allApplications, application => ({
        ...application.user,
        fullName: getCandidateName(application)
      }));
    },
    formattedInterviewers() {
      return map(this.interviewers, interviewer => ({
        ...interviewer,
        fullName: getFullName(interviewer)
      }));
    },
    ...mapInterviewState(copyState(["interviewers", "isFetchingInterviewers"])),
    currentBookingId() {
      return this.$route.params.id;
    }
  },
  methods: {
    ...mapBookingActions({
      fetchCurrentBookingApplications: FETCH_CURRENT_BOOKING_CANDIDATES
    }),
    ...mapInterviewActions({
      fetchInterviewers: FETCH_INTERVIEWERS
    }),
    handleChange(key, value) {
      this.$emit("onChange", {
        key,
        value
      });
    },
    updateData(key, value) {
      this.interviewSlotData[key] = value;
      this.handleChange(key, value);
    },
    unselectInterviewer(interviewerId) {
      this.updateData(
        "interviewers",
        filter(this.interviewSlotData.interviewers, id => id !== interviewerId)
      );
    },
    async validateForm() {
      this.showValidationErrors = true;
      const isValid = await this.$refs.interviewSlot.validate();
      return isValid && this.interviewSlotData.slot;
    },
    size
  },
  watch: {
    interviewFormatId(newValue, prevValue) {
      if (newValue !== prevValue) {
        this.interviewSlotData.interviewers = [];
        this.interviewSlotData.slot = null;
        this.fetchInterviewers(newValue);
      }
    }
  }
};
</script>

<style lang="scss"></style>
